import { PageLayout } from '@app/pages/layout/PageLayout'
import { useQuery } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import Row from '@app/components/layout/Row'
import useTransform from '@app/util/useTransform'
import Column from '@app/components/layout/Column'
import { TabContent } from './sections/TabContent'
import { ItemListCard } from '@app/components/blocks/card/ItemListCard'
import useMediaQuery from '@app/util/useMediaQuery'
import { useLocation } from 'wouter'
import { ServiceProviderConceptViewModel } from 'functions/app/service-provider-concept/view'
import { ServiceProviderConceptPageHeaderCard } from './ServiceProviderConceptPageHeaderCard'
import { pages } from '@app/util/links'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { useParamState } from '@app/util/hooks/useParamState'
import {
  serviceProviderConceptTabs,
  serviceProviderConceptTabsQueryParamName,
} from './constants'
import { shouldNoindexPageTab } from '@app/util/seo/shouldNoindexPageTab'

export type ServiceProviderConceptPageProps = {
  slug: string
}

export const ServiceProviderConceptPage = ({
  slug,
}: ServiceProviderConceptPageProps) => {
  const t = useTranslations().app.serviceProviderConceptPage
  const transformStyle = useTransform()
  const isMobile = useMediaQuery()
  const [, setLocation] = useLocation()
  const serviceProviderConceptQuery = useQuery('view-service-provider-concept', {
    slug,
  })
  const serviceProviderConcept: ServiceProviderConceptViewModel | undefined =
    serviceProviderConceptQuery.data

  const pageStatus = getEntityPageStatus(
    serviceProviderConceptQuery.loading,
    serviceProviderConcept,
  )

  if (pageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (pageStatus === 'loading') {
    return <div>{t.loading}</div>
  }

  return (
    <PageLayout gap="2rem">
      <PageHead serviceProviderConcept={serviceProviderConcept} />
      <Row
        gap="3rem"
        style={{
          alignItems: 'flex-start',
          ...transformStyle,
        }}
      >
        <Column style={{ flex: 1, width: '100%' }} gap="1.5rem">
          <ServiceProviderConceptPageHeaderCard
            serviceProviderConcept={serviceProviderConcept}
          />
          <TabContent serviceProviderConcept={serviceProviderConcept} />
        </Column>

        {!isMobile && (
          <Column style={{ flex: '0 1 23rem', minWidth: '23rem' }}>
            <ItemListCard
              title={t.offeredBy}
              items={serviceProviderConcept.serviceProviders.map((sp) => ({
                title: sp.name,
                image: sp.avatar?.src,
                onClick: () => setLocation(`${pages.serviceProvider(sp.slug)}`),
              }))}
            />
          </Column>
        )}
      </Row>
    </PageLayout>
  )
}

const PageHead = ({
  serviceProviderConcept,
}: {
  serviceProviderConcept: ServiceProviderConceptViewModel
}) => {
  const [tab] = useParamState(
    serviceProviderConceptTabs,
    serviceProviderConceptTabsQueryParamName,
    serviceProviderConceptTabs.overview,
  )

  return (
    <PageSeo
      pageTitle={serviceProviderConcept.name}
      description={serviceProviderConcept.body}
      image={serviceProviderConcept.banner}
      noindex={shouldNoindexPageTab(tab)}
    />
  )
}
